import { Rs232Controller } from './Rs232Controller';
import { Rs232DisplayAdapter } from './Rs232DisplayAdapter';

/* eslint-disable no-unused-vars */
// The following Display Adapter classes are used in
// `Rs232Display.createOrOpen()` via the `adapterClassName` variable.

import { NecRs232DisplayAdapter } from './NecRs232DisplayAdapter';
import { SamsungRs232DisplayAdapter } from './SamsungRs232DisplayAdapter';
import { ViewSonicRs232DisplayAdapter } from './ViewSonicRs232DisplayAdapter';
import { LgRs232DisplayAdapter } from './LgRs232DisplayAdapter';
/* eslint-enable no-unused-vars */

import { $ } from '../../../vendor/jquery';
import _ from 'underscore';
import { Log } from '../../AuxillaryService/Log';

const _adapters = {
    SamsungRs232DisplayAdapter: SamsungRs232DisplayAdapter,
    ViewSonicRs232DisplayAdapter: ViewSonicRs232DisplayAdapter,
    LgRs232DisplayAdapter: LgRs232DisplayAdapter,
    NecRs232DisplayAdapter: NecRs232DisplayAdapter,
};

/**
 * Settings for COM communication
 * @typedef {Object} ComSettings
 * @property {integer} baudRate
 * @property {integer} dataBits
 * @property {string} stopBits
 * @property {string} parity
 * @property {bool} discardNull
 * @property {string} encoding
 * @property {string} handshake
 * @property {string} newLine
 * @property {integer} readBufferSize
 * @property {integer} readBytesThreshold
 * @property {bool} dtrEnabled
 * @property {bool} rtsEnabled
 * @property {integer} writeBufferSize
 */

/**
 * Collection COM port RS232Controller objects which have been created
 * @type {{}}
 * @private
 */
var _openComPorts = {};

/**
 * Represents a physical display device
 * @class
 * @hideconstructor
 */
function Rs232Display() {}

/**
 * creates an Rs232DisplayAdapter on the given COM port using an adapter for the
 * given hardware make
 * @param {string} comPort - the name of the COM port to open
 * @param {string|null} displayMake - the make of the display device
 * @param {string|null} displayModel - the model of the display device
 * @param {ComSettings|null} comSettings - settings for
 * the com port
 * @returns {Rs232DisplayAdapter}
 * @static
 */
Rs232Display.createOrOpen = function (comPort, displayMake, displayModel, comSettings) {
    var com = Rs232Display.open(comPort);

    if (com) {
        return com;
    }

    try {
        comSettings = comSettings || {};
        const adapterClassName =
            displayMake.charAt(0).toUpperCase() + displayMake.substr(1) + 'Rs232DisplayAdapter';
        const adapterClass = _adapters[adapterClassName];

        // add the com settings based on the display make
        $.extend(comSettings, adapterClass.comSettings);
    } catch (error) {
        // The original developer failed to document what exception they were
        // trying to catch, so it is unknown why this try/catch block is here.
        Log.Error('Could not create adapter. Message: ' + error.message, null, __fileline);
    }

    return Rs232Display.create(comPort, displayMake, displayModel, comSettings);
};

/**
 * opens an existing Rs232DisplayAdapter on the given COM port
 * @param {string} comPort - the name of the COM port to open
 * @returns {Rs232DisplayAdapter}
 * @static
 */
Rs232Display.open = function (comPort) {
    if (_openComPorts[comPort]) {
        return _openComPorts[comPort];
    }

    return null;
};

/**
 * creates a new existing Rs232DisplayAdapter on the given COM port for given
 * hardware and settings
 * @param {string} comPort - the name of the COM port to open
 * @param {string|null} displayMake - the make of the display device
 * @param {string|null} displayModel - the model of the display device
 * @param {ComSettings|null} comSettings - settings for
 * the com port
 * @returns {Rs232DisplayAdapter}
 * @static
 */
Rs232Display.create = function (comPort, displayMake, displayModel, comSettings) {
    if (_openComPorts[comPort]) {
        throw new Error('COM port `' + comPort + '` already open');
    }

    var com = new Rs232Controller.createRs232Controller(comPort, comSettings);

    com.open();

    _openComPorts[comPort] = new Rs232DisplayAdapter.createVendorAdapter(
        displayMake,
        displayModel,
        com
    );

    return _openComPorts[comPort];
};

/**
 * Closes and removes an Rs232Controller by name (e.g. COM1, COM2, etc.)
 * @param {string} comPort
 * @static
 */
Rs232Display.destroy = function (comPort) {
    if (_openComPorts[comPort]) {
        try {
            _openComPorts[comPort].controller.close();
        } catch (error) {
            // The original developer failed to document what exception they
            // were trying to catch, so it is unknown why this try/catch block
            // is here.
            Log.Error('Could not destroy adapter. Message: ' + error.message, null, __fileline);
        }

        delete _openComPorts[comPort];
    }
};

/**
 * Probes the next vendor/port combination in portList. Resolves jqDeferred if
 * there are no more items left in portList.
 *
 * @todo convert to A+ Promises
 * @param {Array.<{}>} portList - list of vendor/port combinations to test
 * @param {$.Deferred} jqDeferred - deferred to resolve when no more items are
 * left in portList
 * @param {Array.<{}>} resultList - list of vendor/port combinations that have
 * been discovered
 * @private
 */
var _probeNextPort = function (portList, jqDeferred, resultList) {
    if (portList.length < 1) {
        jqDeferred.resolve(resultList);

        return;
    }

    var nextItem = portList.shift();

    try {
        var com = Rs232Display.createOrOpen(nextItem.comPort, nextItem.displayMake);

        com.getModel().then(
            function () {
                resultList.push({
                    comPort: nextItem.comPort,
                    displayMake: nextItem.displayMake,
                });

                Rs232Display.destroy(nextItem.comPort);

                _probeNextPort(portList, jqDeferred, resultList);
            },
            function (e) {
                Log.Error('Could not get model. Message: ' + e.message, null, __fileline);

                Rs232Display.destroy(nextItem.comPort);

                _probeNextPort(portList, jqDeferred, resultList);
            }
        );
    } catch (e) {
        Log.Error('Could not run probe. Message: ' + e.message, null, __fileline);

        Rs232Display.destroy(nextItem.comPort);

        _probeNextPort(portList, jqDeferred, resultList);
    } //failed to open COM port
};

/**
 * Probes every combination of COM port and vendor. Probes are conducted in
 * parallel across COM ports. That is to say that this function will probe COM1
 * and COM2 in parallel.
 * @todo convert to A+ Promises
 * @returns {Promise}
 */
Rs232Display.probeComPorts = function () {
    var allDeferreds = [];
    var jqDeferred = $.Deferred();

    Rs232Controller.enumerateComPorts().forEach(function (comPortName) {
        //NOTE: if the COM port is already in use, it will disrupt the probe
        Rs232Display.destroy(comPortName);

        //NOTE: we can scan com ports in tandem, so we create a collection of
        //      vendors to probe for for each COM port and start separate
        //      recursive probes simultaneously
        var combinations = [];
        var dfd = $.Deferred();

        Rs232DisplayAdapter.enumerateVendorNames().forEach(function (vendor) {
            combinations.push({
                comPort: comPortName,
                displayMake: vendor,
            });
        });

        _probeNextPort(combinations, dfd, []);

        allDeferreds.push(dfd);
    });

    $.when.apply($, allDeferreds).done(function () {
        jqDeferred.resolve(_.flatten(arguments));
    });

    return jqDeferred.promise();
};

export { Rs232Display };
